import { ENDPOINTS } from '@shared/utils'
import JsonApi from 'devour-client'
import { globalStoreForLogging } from 'src/App.utils'
import { CATEGORY_SURVEY_RESULT } from 'src/pages/CustomerQualification/Survey/CustomerSurvey.const'
import {
  getAccessToken,
  getUrl,
  setAccessToken,
  setPrecisionsInSessionStorage,
} from 'src/services/AppContext'
import { deserialize } from 'src/services/deserializers/deserializers'
import {
  AGGREGATED_SERIES_MODEL,
  AGGREGATION_ATTRIBUTES,
  CIRCUIT_ATTRIBUTES,
  GENERIC_MODEL,
  LEVERS_ATTRIBUTES,
  OPTION_ATTRIBUTES,
} from 'src/services/json-api/json-api.const'
import { GRANT_TYPE } from 'src/services/request.const'
import { serialize2 } from 'src/services/serializers/serializers'
import { OBJECT_TYPES } from 'src/tpbp-configuration/models/models.const'
import {
  CUSTOM_HEADERS,
  LOCAL_STORAGE_KEYS,
} from 'src/tpbp-core/models/common.const'
import { lclBridgeService } from 'src/services/bridge/LclBridgeService'
import { LCL_BRIDGE_COMMAND } from 'src/services/bridge/LclBridge.const'
import apiClient from '../api/api-client.service'
import { getMockPagePath, getMockScenario } from '../utils/mock-scenario.utils'
import { customParametersSerializer } from './custom-parameters-serializer.utils'
import type {
  JsonApiContext,
  JsonApiInstance,
  JsonApiPayload,
  JsonApiResponse,
  TokenSet,
} from './json-api.types'

export type JsonApiOptions = NonNullable<
  Parameters<ReturnType<JsonApiInstance['all']>['get']>[0]
>

export const jsonApi: JsonApiInstance = new JsonApi({
  apiUrl: '',
  pluralize: false,
})

jsonApi.runMiddleware = (request) => {
  const payload: JsonApiPayload = { req: request, jsonApi }
  let requestPromise = Promise.resolve(payload)
  requestPromise = jsonApi.applyRequestMiddleware(requestPromise)

  return requestPromise
    .then((response) => {
      payload.res = response
      const responsePromise = Promise.resolve(payload)

      return jsonApi.applyResponseMiddleware(responsePromise)
    })
    .catch((error) => {
      const errorPromise = Promise.resolve(error)

      return jsonApi
        .applyErrorMiddleware(errorPromise)
        .then((response) => response)
        .catch((error) => error)
    })
}

const context: JsonApiContext = {
  modalOpened: false,
  _on401Error: null,
  _onError: null,
}

const define = (objectType) => {
  jsonApi.define(objectType, null, {
    deserializer: deserialize,
    serializer: serialize2,
  })
}

for (const property in OBJECT_TYPES) {
  if (Object.prototype.hasOwnProperty.call(OBJECT_TYPES, property)) {
    define(OBJECT_TYPES[property])
  }
}

jsonApi.define('circuits', CIRCUIT_ATTRIBUTES)
jsonApi.define('levers', LEVERS_ATTRIBUTES)
jsonApi.define('lever', LEVERS_ATTRIBUTES)
jsonApi.define('options', OPTION_ATTRIBUTES)
jsonApi.define('genericModel', GENERIC_MODEL)
jsonApi.define(CATEGORY_SURVEY_RESULT.pi, GENERIC_MODEL)
jsonApi.define(CATEGORY_SURVEY_RESULT.qce, GENERIC_MODEL)
jsonApi.define('retirement_complementary_info', GENERIC_MODEL)
jsonApi.define('aggregations', AGGREGATION_ATTRIBUTES)
jsonApi.define('aggregated_series', AGGREGATED_SERIES_MODEL)

const parameterSerializer = {
  name: 'rails-params-serializer-bis',
  req: function request(payload) {
    if (payload.req.method === 'GET') {
      payload.req.paramsSerializer = customParametersSerializer
    }

    return payload
  },
}

const beforeRequestMiddleware = {
  name: 'add-bearer-token-if',
  req: (payload) => {
    jsonApi._lastErrors = null
    if (payload.req.method === 'PATCH') {
      delete payload.req.data.meta
    }
    const token = getAccessToken()
    if (token) {
      payload.req.headers.Authorization = `Bearer ${token}`
    }

    payload.req.headers['Cache-Control'] = 'no-store, no-cache'
    payload.req.headers.Pragma = 'no-cache'

    return payload
  },
}

export function setOnErrorCallback(on401Error, onError) {
  context._on401Error = on401Error
  context._onError = onError
}

const errorMiddleware = {
  name: 'errorHandler',
  async error(payload): Promise<JsonApiResponse | undefined> {
    const isSynchroniseTimeoutError = payload?.response?.status === 504
    if (isSynchroniseTimeoutError) {
      return // do not show any error because this behavior is expected for now
    }
    if (payload?.response?.status === 401) {
      // token expiration case
      // inspiré par https://medium.com/swlh/handling-access-and-refresh-tokens-using-axios-interceptors-3970b601a5da
      const originalRequest = payload.config
      if (context.modalOpened || originalRequest._isRetrying) {
        return
      }
      context.modalOpened = true
      if (!context._on401Error) {
        throw new Error('context._on401Error is not defined')
      }
      const response = await context._on401Error()
      context.modalOpened = false

      if (!response) {
        localStorage.clear()
        window.location.reload()
      }
      await requestTokenWithRefreshToken(
        localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN),
      )
      originalRequest._isRetrying = true
      const { data } = originalRequest.data
        ? JSON.parse(originalRequest.data)
        : { data: null }
      const originalResult = await jsonApi.request(
        originalRequest.url,
        originalRequest.method.toUpperCase(),
        originalRequest.params,
        data,
      )
      context.modalOpened = false
      if (originalResult instanceof Error) throw originalResult
      return originalResult
    }
    if (!context._onError) {
      throw new Error('context._onError is not defined')
    }
    context._onError(payload?.response)
    if (payload?.response?.data?.errors?.length > 0) {
      jsonApi._lastErrors = payload.response.data.errors
      const message = [
        payload.response.data.errors[0].meta?.attribute,
        payload.response.data.errors[0].detail,
      ].join(', ')

      throw new Error(message, { cause: payload.response.status })
    }
    throw new Error(payload.message ?? 'unhandled error in errorMiddleware')
  },
}

const requestMiddleware = {
  name: 'axios-request-2',
  req: function request(payload) {
    const { jsonApi, req } = payload

    if (req?.data?.meta) {
      // remove unexpected meta attribute
      delete req.data.meta
    }

    if (!req.url.includes('://')) {
      if (req.params?.useTpbpPrefix === undefined) {
        req.url = getUrl(req.url, true)
      } else {
        req.url = getUrl(req.url, !!req.params.useTpbpPrefix)
        delete req.params.useTpbpPrefix
      }
    }

    return jsonApi.axios(req)
  },
}
function _getBackEndErrors() {
  return jsonApi._lastErrors || []
}

export async function login(
  username: string,
  password: string,
): Promise<TokenSet> {
  const parameters = { username, password, grant_type: GRANT_TYPE.password }
  const url = ENDPOINTS.oauthToken()
  const response = await apiClient.post<TokenSet>(url, parameters, {
    useTpbpPrefix: false,
    omitToken: true,
  })
  if (!response.data) throw new Error('No data found in login response')
  return response.data
}

export function clearStoredToken() {
  saveTokenToLocalStorage()
}

export async function requestTokenWithPassword(
  username: string,
  password: string,
) {
  clearStoredToken()
  const data = await login(username, password)
  saveTokenToLocalStorage(data)
  if (!data.access_token) throw new Error('No access token found in response')
  return data.access_token
}

export function saveTokenToLocalStorage(
  token?: TokenSet,
  overrideNniInfo = true,
) {
  setAccessToken(token?.access_token || '')
  setPrecisionsInSessionStorage(token?.precisions ?? [])
  if (overrideNniInfo) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ROLE, token?.kind || '')
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.TARGET_ORIGIN,
      token?.target_origin || '',
    )
  }
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
    token?.refresh_token || '',
  )
}

export async function requestTokenWithRefreshToken(refreshToken) {
  globalStoreForLogging().startLog('requestTokenWithRefreshToken')
  globalStoreForLogging().startLog('computeUrl')
  const url = ENDPOINTS.oauthToken()
  globalStoreForLogging().endLog(`compute url ${url}`)
  const parameters = {
    grant_type: GRANT_TYPE.refreshToken,
    refresh_token: refreshToken,
  }
  const response = await apiClient.post(url, parameters, {
    useTpbpPrefix: false,
    omitToken: true,
  })
  const precisions = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
  const tokenSet: TokenSet = {
    ...response.data,
    precisions: `${response.data.precisions || precisions}`,
  }
  saveTokenToLocalStorage(tokenSet, false)
  globalStoreForLogging().endLog('report-loadIndicators')
  return response.data.access_token
}

export async function logout() {
  await apiClient.post(ENDPOINTS.oauthRevoke(), undefined, {
    useTpbpPrefix: false,
  })
  lclBridgeService.sendCommand(LCL_BRIDGE_COMMAND.CLOSE_CONTAINER_COMMAND, {
    status: 'abort',
  })
  clearStoredToken()
}

jsonApi._getBackEndErrors = _getBackEndErrors
jsonApi.insertMiddlewareBefore('axios-request', beforeRequestMiddleware)
jsonApi.replaceMiddleware('errors', errorMiddleware)
jsonApi.replaceMiddleware('rails-params-serializer', parameterSerializer)
jsonApi.replaceMiddleware('axios-request', requestMiddleware)

jsonApi.$serialize = serialize2

jsonApi.axios.interceptors.request.use((config) => {
  const mockScenario = getMockScenario()
  const mockPagePath = getMockPagePath()

  if (mockScenario)
    config.headers.set(CUSTOM_HEADERS.MOCK_SCENARIO, mockScenario)

  if (mockPagePath)
    config.headers.set(CUSTOM_HEADERS.MOCK_PAGE_PATH, mockPagePath)

  return config
})
